<template>
  <div class="contest-home">
    <section class="relative min-h-[620px] w-full overflow-hidden flex items-center bg-surface-container px-8">
      <div class="absolute inset-0 z-0">
        <img v-if="heroBackgroundImage" :src="heroBackgroundImage" alt="" class="w-full h-full object-cover" />
        <div class="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary-container/50"></div>
      </div>
      <div class="relative z-10 max-w-[1440px] mx-auto w-full py-24">
        <div v-if="redirectNotice" class="mb-8 rounded-2xl border border-white/20 bg-white/10 px-6 py-4 text-white shadow-sm backdrop-blur-sm">
          {{ redirectNotice }}
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-10 items-center">
          <div>
            <div class="inline-flex items-center rounded-full bg-white/12 px-4 py-2 text-sm font-semibold text-white/90">
              门户入口仅承担宣传导流，请通过微信小程序参与正式业务
            </div>
            <h1 class="mt-6 text-white font-headline text-5xl font-extrabold leading-tight tracking-tight">
              {{ activity.activityName || '“人人都是创新官”线上竞赛' }}
            </h1>
            <p class="mt-6 text-surface-container-low text-xl leading-relaxed max-w-3xl">
              {{ heroSummary }}
            </p>
            <div class="mt-10 flex flex-wrap gap-4">
              <button class="bg-secondary text-white px-8 py-4 rounded-lg font-bold hover:-translate-y-1 transition-transform" @click="scrollToSection('guide')">
                我要参与
              </button>
              <button class="bg-white/10 text-white border border-white/20 px-8 py-4 rounded-lg font-bold hover:bg-white/15 transition-colors" @click="scrollToSection('schedule')">
                查看时间安排
              </button>
            </div>
          </div>
          <div class="rounded-3xl bg-white/10 p-8 text-white shadow-xl backdrop-blur-sm">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="rounded-2xl bg-white/10 p-5">
                <div class="text-sm text-white/70">当前阶段</div>
                <div class="mt-2 text-2xl font-bold">{{ stageLabel }}</div>
              </div>
              <div class="rounded-2xl bg-white/10 p-5">
                <div class="text-sm text-white/70">主办单位</div>
                <div class="mt-2 text-lg font-bold leading-snug">{{ activity.organizer || '待后台配置' }}</div>
              </div>
              <div class="rounded-2xl bg-white/10 p-5 sm:col-span-2">
                <div class="text-sm text-white/70">参与方式</div>
                <div class="mt-2 text-lg font-bold leading-snug">微信小程序为一期正式业务入口，门户仅提供活动说明与进入指引。</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="intro" class="py-24 bg-surface px-8">
      <div class="max-w-[1440px] mx-auto">
        <div class="bg-surface-container-lowest rounded-3xl p-10 shadow-sm">
          <h2 class="text-primary font-headline text-3xl font-bold border-l-4 border-secondary pl-4">活动介绍</h2>
          <div class="contest-rich-text mt-8 text-on-surface-variant leading-relaxed text-lg" v-html="activity.introContent || defaultIntro"></div>
        </div>
      </div>
    </section>

    <section id="schedule" class="py-24 bg-surface-container-low px-8">
      <div class="max-w-[1440px] mx-auto">
        <h2 class="text-primary font-headline text-3xl font-bold border-l-4 border-secondary pl-4">时间安排</h2>
        <div class="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="bg-surface-container-lowest rounded-3xl p-8 shadow-sm">
            <div class="text-sm font-semibold text-secondary uppercase tracking-wider">第一阶段：投稿期</div>
            <div class="mt-4 text-primary font-bold text-xl">作品征集</div>
            <div class="mt-3 text-on-surface-variant leading-relaxed">
              {{ formatRange(activity.submissionStartTime, activity.submissionEndTime) }}
            </div>
          </div>
          <div class="bg-surface-container-lowest rounded-3xl p-8 shadow-sm">
            <div class="text-sm font-semibold text-secondary uppercase tracking-wider">第二阶段：投稿+投票期</div>
            <div class="mt-4 text-primary font-bold text-xl">公众互动</div>
            <div class="mt-3 text-on-surface-variant leading-relaxed">
              {{ formatRange(activity.voteStartTime, activity.voteEndTime) }}
            </div>
          </div>
          <div class="bg-surface-container-lowest rounded-3xl p-8 shadow-sm">
            <div class="text-sm font-semibold text-secondary uppercase tracking-wider">第三阶段：结果展示期</div>
            <div class="mt-4 text-primary font-bold text-xl">最终结果发布</div>
            <div class="mt-3 text-on-surface-variant leading-relaxed">
              {{ activity.resultPublishTime ? formatDateTime(activity.resultPublishTime) : '待后台配置' }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="rules" class="py-24 bg-surface px-8">
      <div class="max-w-[1440px] mx-auto">
        <div class="bg-surface-container-lowest rounded-3xl p-10 shadow-sm">
          <h2 class="text-primary font-headline text-3xl font-bold border-l-4 border-secondary pl-4">活动规则</h2>
          <div class="contest-rich-text mt-8 text-on-surface-variant leading-relaxed text-lg" v-html="activity.ruleContent || defaultRule"></div>
        </div>
      </div>
    </section>

    <section id="guide" class="py-24 bg-surface-container-low px-8">
      <div class="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-10 items-stretch">
        <div class="bg-surface-container-lowest rounded-3xl p-10 shadow-sm flex flex-col justify-between">
          <div>
            <h2 class="text-primary font-headline text-3xl font-bold border-l-4 border-secondary pl-4">微信小程序参与指引</h2>
            <div class="contest-rich-text mt-8 text-on-surface-variant text-lg leading-relaxed" v-html="activity.guideContent || defaultGuide"></div>
          </div>
        </div>
        <div class="bg-surface-container-lowest rounded-3xl p-10 shadow-sm flex flex-col items-center justify-center text-center">
          <div v-if="activity.miniprogramQrCode" class="w-[260px] h-[260px] overflow-hidden rounded-3xl border border-surface-container shadow-sm bg-white">
            <img :src="resolveMediaUrl(activity.miniprogramQrCode)" alt="微信小程序码" class="w-full h-full object-contain" />
          </div>
          <div v-else class="w-[260px] h-[260px] rounded-3xl border-2 border-dashed border-surface-container-high bg-surface-container-low flex flex-col items-center justify-center text-outline">
            <span class="material-symbols-outlined text-6xl mb-4">qr_code_2</span>
            <span class="text-base font-semibold">微信小程序码占位区</span>
            <span class="mt-2 text-sm">上线前可替换为正式小程序码图片</span>
          </div>
          <div class="mt-8 max-w-xl text-on-surface-variant leading-relaxed">
            微信扫码报名参与。
          </div>
          <div class="mt-8 flex w-full justify-end">
            <button class="bg-surface-container text-primary px-8 py-4 rounded-lg font-bold hover:bg-surface-container-high transition-colors" @click="scrollToSection('intro')">
              返回活动介绍
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { fetchContestCurrentActivity } from '@/utils/contest'
import { resolveMediaUrl } from '@/utils/mediaUrl'

export default {
  name: 'ContestHome',
  data () {
    return {
      activity: {},
      loading: false,
      redirectTextMap: {
        login: '门户端已不再提供登录注册，请前往微信小程序完成授权登录。',
        square: '门户端已不再提供作品广场浏览与投票，请前往微信小程序参与。',
        ranking: '门户端已不再提供实时排行榜业务页，请前往微信小程序查看与参与。',
        detail: '门户端已不再提供作品详情与互动，请前往微信小程序参与。',
        'user-center': '门户端已不再提供个人中心，请前往微信小程序查看个人信息与数据。',
        'my-submissions': '门户端已不再提供投稿管理，请前往微信小程序进行投稿与查看记录。'
      },
      defaultIntro: '<p>本次活动聚焦基层管理创新成果展示，面向企业及基层单位征集优秀管理实践案例，通过数字化方式统一发布活动信息并引导用户进入微信小程序参与。</p>',
      defaultRule: '<p>活动按投稿期、投稿加投票期、结果展示期三个阶段推进。门户页仅展示活动规则、时间安排和进入指引，正式投稿、投票、点赞与个人中心能力请在微信小程序内完成。本期正式用户端为微信小程序。投稿、投票、点赞、个人中心等业务能力均在小程序内完成，门户页仅保留活动宣传与进入指引。</p>',
      defaultGuide: '<p>本期正式用户端为微信小程序。投稿、投票、点赞、个人中心等业务能力均在小程序内完成，门户页仅保留活动宣传与进入指引。</p>'
    }
  },
  computed: {
    heroBackgroundImage () {
      return this.resolveMediaUrl(this.activity.bannerImage)
    },
    stageLabel () {
      const map = {
        submission: '投稿期',
        voting: '投稿+投票期',
        result: '结果展示期'
      }
      return map[this.activity.activityStage] || '待后台配置'
    },
    heroSummary () {
      if (this.activity.introContent) {
        return this.stripHtml(this.activity.introContent).slice(0, 90) || '发掘基层管理创新案例，展示卓越管理成果，激发企业创新活力。'
      }
      return '发掘基层管理创新案例，展示卓越管理成果，激发企业创新活力。'
    },
    redirectNotice () {
      const from = this.$route.query.from
      return this.redirectTextMap[from] || ''
    }
  },
  created () {
    this.loadActivity()
  },
  mounted () {
    this.scrollFromRoute()
  },
  watch: {
    '$route.query': {
      handler () {
        this.$nextTick(() => {
          this.scrollFromRoute()
        })
      },
      deep: true
    }
  },
  methods: {
    resolveMediaUrl,
    async loadActivity () {
      this.loading = true
      try {
        const data = await fetchContestCurrentActivity()
        this.activity = data || {}
      } catch (error) {
        console.error('[ContestHome] 加载当前活动配置失败', {
          endpoint: ['/dev-api/contest/open/activity/current', '/prod-api/contest/open/activity/current'],
          message: error && error.message ? error.message : error
        })
        this.activity = {}
      } finally {
        this.loading = false
      }
    },
    scrollFromRoute () {
      const section = this.$route.query.section
      if (!section) {
        return
      }
      const el = document.getElementById(section)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    scrollToSection (section) {
      if (this.$route.query.section !== section) {
        this.$router.replace({
          path: '/contest/home',
          query: Object.assign({}, this.$route.query, { section: section })
        })
        return
      }
      const el = document.getElementById(section)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    formatDateTime (value) {
      if (!value) {
        return '待后台配置'
      }
      const date = new Date(String(value).replace(/-/g, '/'))
      if (Number.isNaN(date.getTime())) {
        return value
      }
      const year = date.getFullYear()
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      const hour = `${date.getHours()}`.padStart(2, '0')
      const minute = `${date.getMinutes()}`.padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}`
    },
    formatRange (start, end) {
      const startText = this.formatDateTime(start)
      const endText = this.formatDateTime(end)
      if (startText === '待后台配置' && endText === '待后台配置') {
        return '待后台配置'
      }
      return `${startText} 至 ${endText}`
    },
    stripHtml (value) {
      return String(value || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
  }
}
</script>

<style scoped>
.contest-rich-text {
  word-break: break-word;
}

.contest-rich-text >>> p {
  margin-bottom: 16px;
}

.contest-rich-text >>> h1,
.contest-rich-text >>> h2,
.contest-rich-text >>> h3,
.contest-rich-text >>> h4 {
  margin-top: 24px;
  margin-bottom: 12px;
  color: var(--el-text-color-primary, #1f2937);
  font-weight: 700;
}

.contest-rich-text >>> ul,
.contest-rich-text >>> ol {
  padding-left: 20px;
  margin-bottom: 16px;
}

.contest-rich-text >>> li {
  margin-bottom: 8px;
}
</style>



// WEBPACK FOOTER //
// src/contest/views/ContestHome.vue